import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { TutorialStep } from '../components/TutorialTooltip';
import { postSurveyFill } from '../services/survey/surveyService';
import { decodeTokenService } from '../services/decodeTokenService';

interface TutorialContextType {
  isWelcomeOpen: boolean;
  isTutorialActive: boolean;
  currentStep: TutorialStep;
  currentStepIndex: number;
  totalSteps: number;
  startTutorial: () => void;
  nextStep: () => void;
  closeTutorial: () => void;
  restartTutorial: () => void;
  setSteps: (steps: TutorialStep[]) => void;
  setTutorialActions: (actions: Record<string, () => void>) => void;
}

const TutorialContext = createContext<TutorialContextType | undefined>(undefined);

export function TutorialProvider({ children }: { children: ReactNode }) {
  const [steps, setSteps] = useState<TutorialStep[]>([]);
  const [isWelcomeOpen, setIsWelcomeOpen] = useState(true);
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [tutorialActions, setTutorialActions] = useState<Record<string, () => void>>({});

  const currentStep = isTutorialActive && currentStepIndex < steps.length
    ? steps[currentStepIndex]
    : steps[0];

  const handlePostSurveyResponse = ({ id, answer }: { id: string; answer: any }) => {
    const payload = {
      entity_type: "user",
      entity_id: decodeTokenService.getUserIdFromToken() as string,
      answers: {
        [id]: answer,
      }
    };
    postSurveyFill(payload, 'create-funnel-tutorial')
      .then((res) => {
        console.log(res);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  const startTutorial = useCallback(() => {
    setIsWelcomeOpen(false);
    setIsTutorialActive(true);
    setCurrentStepIndex(0);
  }, []);

  const nextStep = useCallback(() => {
    console.log("next step", currentStep?.id);
    if (currentStepIndex < steps.length - 1) {
      const stepId = currentStep?.id;

      if (stepId && tutorialActions[stepId]) {
        tutorialActions[stepId]();
      }

      setCurrentStepIndex(prev => prev + 1);
    } else {
      const stepId = currentStep?.id;
      if (stepId && tutorialActions[stepId]) {
        tutorialActions[stepId]();
      }
      setIsTutorialActive(false);
      setCurrentStepIndex(0);
    }
  }, [currentStepIndex, steps.length, currentStep, tutorialActions]);

  const closeTutorial = useCallback(() => {
    setIsTutorialActive(false);
    setIsWelcomeOpen(false);
    setCurrentStepIndex(0);
  }, []);

  const restartTutorial = useCallback(() => {
    setIsWelcomeOpen(true);
    setIsTutorialActive(false);
    setCurrentStepIndex(0);
  }, []);

  return (
    <TutorialContext.Provider
      value={{
        isWelcomeOpen,
        isTutorialActive,
        currentStep,
        currentStepIndex,
        totalSteps: steps.length,
        startTutorial,
        nextStep,
        closeTutorial,
        restartTutorial,
        setSteps,
        setTutorialActions,
      }}
    >
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorialContext() {
  const context = useContext(TutorialContext);
  if (context === undefined) {
    throw new Error('useTutorialContext must be used within a TutorialProvider');
  }
  return context;
}
